import { useEffect, useId, useRef, useState } from 'react';
import { usePwaTheme } from '@/context/PwaThemeContext';
import { forcePwaUpdate } from '@/lib/forcePwaUpdate';

type AppHeaderMenuProps = {
  userLabel: string | null;
  onLogout: () => void;
};

export function AppHeaderMenu({ userLabel, onLogout }: AppHeaderMenuProps) {
  const { theme, setTheme } = usePwaTheme();
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="app-shell__menu" ref={rootRef}>
      <button
        type="button"
        className="app-shell__menu-trigger"
        aria-label="Menú de cuenta"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
      >
        <svg viewBox="0 0 24 24" width={22} height={22} fill="currentColor" aria-hidden>
          <circle cx="12" cy="5" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="12" cy="19" r="1.75" />
        </svg>
      </button>
      {open ?
        <div id={menuId} className="app-shell__menu-panel" role="menu">
          <div className="app-shell__menu-user" role="presentation">
            {userLabel ?? 'Usuario'}
          </div>
          <div className="app-shell__menu-theme" role="none">
            <span className="app-shell__menu-theme-label" id={`${menuId}-theme-label`}>
              Apariencia
            </span>
            <div
              className="app-shell__theme-toggle"
              role="group"
              aria-labelledby={`${menuId}-theme-label`}
            >
              <button
                type="button"
                className={[
                  'app-shell__theme-option',
                  theme === 'dark' ? 'app-shell__theme-option--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={theme === 'dark'}
                onClick={() => setTheme('dark')}
              >
                Oscuro
              </button>
              <button
                type="button"
                className={[
                  'app-shell__theme-option',
                  theme === 'light' ? 'app-shell__theme-option--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={theme === 'light'}
                onClick={() => setTheme('light')}
              >
                Claro
              </button>
            </div>
          </div>
          <button
            type="button"
            className="app-shell__menu-item"
            role="menuitem"
            disabled={updating}
            onClick={() => {
              setUpdating(true);
              void forcePwaUpdate();
            }}
          >
            {updating ? 'Actualizando…' : 'Actualizar app'}
          </button>
          <button
            type="button"
            className="app-shell__menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Cerrar sesión
          </button>
        </div>
      : null}
    </div>
  );
}
